import { UtensilsCrossed, Building2, GraduationCap, Factory, ShoppingBag } from 'lucide-react'
import { useScrollAnim } from '../../hooks/use-scroll-anim'
import { useI18n } from '../../i18n'

export function UseCasesSection() {
  const { ref, isVisible } = useScrollAnim()
  const { t } = useI18n()

  const useCases = [
    {
      icon: Building2,
      industry: t('金融行业', 'Finance'),
      description: t('合规条款即时查询、客户咨询智能应答、业务流程精准指引，让每位员工都是合规专家', 'Instant compliance lookup, smart customer response, precise process guidance — every employee becomes a compliance expert'),
      tags: [t('合规查询', 'Compliance'), t('智能客服', 'Smart CS'), t('流程指引', 'Process Guide')],
      color: 'text-blue-500',
      hoverBg: 'hover:bg-blue-50/60',
      iconBg: 'bg-blue-50',
      tagHover: 'group-hover:bg-blue-50/80',
    },
    {
      icon: UtensilsCrossed,
      industry: t('餐饮行业', 'Food & Beverage'),
      description: t('菜品标准一键查询、食安规范实时提醒、门店巡检问题追踪，让每家门店运营标准化', 'One-click recipe standards, real-time food safety alerts, store inspection tracking — standardized operations for every location'),
      tags: [t('菜品标准', 'Recipes'), t('食安规范', 'Food Safety'), t('巡检追踪', 'Inspection')],
      color: 'text-orange-500',
      hoverBg: 'hover:bg-orange-50/60',
      iconBg: 'bg-orange-50',
      tagHover: 'group-hover:bg-orange-50/80',
    },
    {
      icon: GraduationCap,
      industry: t('教育培训', 'Education'),
      description: t('课程内容智能问答、教务流程即时查询、学习资料精准检索，让师生零等待获取信息', 'Smart course Q&A, instant academic affairs lookup, precise study material retrieval — zero wait for students and teachers'),
      tags: [t('课程问答', 'Course Q&A'), t('教务查询', 'Academics'), t('资料检索', 'Material Search')],
      color: 'text-amber-600',
      hoverBg: 'hover:bg-amber-50/60',
      iconBg: 'bg-amber-50',
      tagHover: 'group-hover:bg-amber-50/80',
    },
    {
      icon: Factory,
      industry: t('制造行业', 'Manufacturing'),
      description: t('工艺规程秒级查询、设备操作规范指引、质量标准精准检索，让一线生产有据可依', 'Second-level SOP lookup, equipment operation guidance, quality standard retrieval — every production step evidence-based'),
      tags: [t('规程查询', 'SOP Lookup'), t('操作指引', 'Op Guide'), t('标准检索', 'Standards')],
      color: 'text-emerald-500',
      hoverBg: 'hover:bg-emerald-50/60',
      iconBg: 'bg-emerald-50',
      tagHover: 'group-hover:bg-emerald-50/80',
    },
    {
      icon: ShoppingBag,
      industry: t('零售电商', 'Retail & E-commerce'),
      description: t('商品知识精准问答、售后政策即时查询、运营数据智能解读，让一线导购专业如一', 'Precise product knowledge Q&A, instant after-sales policy lookup, smart data interpretation — consistent expertise from every salesperson'),
      tags: [t('商品问答', 'Product Q&A'), t('政策查询', 'Policies'), t('数据解读', 'Data Insights')],
      color: 'text-rose-500',
      hoverBg: 'hover:bg-rose-50/60',
      iconBg: 'bg-rose-50',
      tagHover: 'group-hover:bg-rose-50/80',
    },
  ]

  return (
    <section id="use-cases" className="py-24 min-h-screen flex items-center" style={{ scrollSnapAlign: 'start' }}>
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={`mb-14 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block px-3 py-1 text-xs font-medium text-orange-500 bg-orange-50 rounded-full mb-4">
            {t('行业实践', 'Industry Practice')}
          </span>
          <h2 className="text-2xl md:text-4xl font-semibold text-[#1a1a1a] mb-3 tracking-tight">
            {t('覆盖多行业的企业管家', 'Enterprise Butler Across Industries')}
          </h2>
          <p className="text-sm text-gray-400 max-w-md">
            {t('任何有知识管理和业务咨询需求的行业，都能快速部署专属AI管家', 'Any industry with knowledge management & consultation needs can deploy a dedicated AI Butler rapidly')}
          </p>
        </div>

        <div className="space-y-3">
          {useCases.map((item, i) => {
            const isEven = i % 2 === 0
            return (
              <div
                key={item.industry}
                className={`group rounded-2xl bg-white border border-black/[0.04] p-7 ${item.hoverBg} hover:border-black/[0.08] hover:shadow-lg hover:shadow-black/[0.04] hover:-translate-y-0.5 transition-all duration-500 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                } ${isEven ? 'md:mr-[8%]' : 'md:ml-[8%]'}`}
                style={{ transitionDelay: isVisible ? `${i * 100}ms` : '0ms' }}
              >
                <div className="flex items-start gap-5">
                  <div className={`h-12 w-12 rounded-2xl ${item.iconBg} flex items-center justify-center shrink-0 group-hover:scale-110 group-hover:shadow-sm transition-all duration-300`}>
                    <item.icon className={`h-6 w-6 ${item.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-[#1a1a1a] mb-1.5 group-hover:text-[#000] transition-colors">
                      {item.industry}
                    </h3>
                    <p className="text-sm text-gray-500 leading-relaxed mb-3">
                      {item.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {item.tags.map((tag) => (
                        <span key={tag} className={`px-3 py-1 text-xs rounded-lg bg-gray-50 ${item.tagHover} text-gray-500 border border-black/[0.04] font-medium transition-colors duration-300`}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
